import { useAuth } from "@clerk/clerk-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { isIdentityLinkConflict, useUser } from "@/client/services/user";

type AuthGuardType = {
  referrer?: "DASHBOARD" | "BUILDER" | "PRICING";
};

export const AuthGuard = ({ referrer }: AuthGuardType) => {
  const location = useLocation();
  // Encoded: the destination's own query (?plan=starter&code=…) must survive as one parameter.
  const redirectTo = encodeURIComponent(location.pathname + location.search);

  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const { user, loading, error } = useUser();

  if (loading) return null;

  if (isIdentityLinkConflict(error)) {
    return <Navigate to="/auth/account-link-conflict" replace />;
  }

  if (user) {
    // New Clerk sign-ups are JIT-provisioned without the onboarding step — send them to
    // complete-profile (name/username + legal consents) before letting them into the app.
    if (!user.profileCompletedAt) {
      return <Navigate to={`/auth/complete-profile?redirect=${redirectTo}`} replace />;
    }

    return <Outlet />;
  }

  // Signed in with Clerk but the app's user record hasn't arrived yet (right after sign-in):
  // wait for it rather than bouncing to the sign-in page, which would bounce straight back.
  if (!clerkLoaded || (isSignedIn && !error)) return null;

  if (referrer === "DASHBOARD") {
    window.location.href = `/auth/login?redirect=${redirectTo}`;
    return <Outlet />;
  } else {
    return <Navigate to={`/auth/login?redirect=${redirectTo}`} replace />;
  }
};
